import {
  useCallback, useMemo,
} from 'react';
import { useDispatch } from 'react-redux';
import { CONFIG } from '~/_shared/constants/config';
import { HeatMapStyle } from '~/_shared/types/heatmap/heatMap.enum';
import {
  GroupRadiusEditLevel, ProximityType,
} from '~/_shared/types/proximity/proximity.enums';
import { noop } from '~/_shared/utils/function.helpers';
import { BoundaryGroupCodenames } from '~/boundary/boundary.types';
import { CreateCustomBoundaryMode } from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/createCustomBoundaryGroupPane.component';
import { BoundaryActionType } from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/loadBoundaryPane.component';
import { BoundaryTerritoryType } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroup.type';
import {
  setActiveMapTemplate, setMapSettingsInitialExpandedIndices,
} from '~/store/frontendState/leftSidebar/leftSidebar.actionCreators';
import {
  boundaryFormReset, boundaryFormSetPreselected, boundaryFormSetType, boundaryFormSetTypeAndMode,
} from '~/store/frontendState/mapTools/boundary/boundaryForm/boundaryForm.actionCreators';
import { heatmapFormInitialStyleSet } from '~/store/frontendState/mapTools/heatmap/heatmapForm.actionCreators';
import {
  setApplyTo, setProximityWithin, setTravelTimeHours, setTravelTimeMinutes, setType,
} from '~/store/frontendState/proximityTool/proximityTool.actionCreators';
import { useClientIdSelector } from '~/store/selectors/useClientIdSelector';
import { useMapIdSelector } from '~/store/selectors/useMapIdSelector';
import { MapTemplates } from './mapTemplates.helpers';
import { useRecordTemplateUsage } from './useMapTemplatesUsage.hook';

export const useMapPageMapTemplatesCallbacks = ({
  onSkip,
  onTemplateSelected,
}: Readonly<{
  onSkip?: () => void;
  onTemplateSelected?: (template: MapTemplates) => void;
}> = {}) => {
  const dispatch = useDispatch();
  const clientId = useClientIdSelector();
  const mapId = useMapIdSelector();
  const { create: createTemplateUsageRecord } = useRecordTemplateUsage();

  const callbacks = useMemo((): Record<MapTemplates, () => void> => ({
    [MapTemplates.ATB]: () => {
      dispatch(boundaryFormSetTypeAndMode(
        BoundaryActionType.CreateTerritories,
        CONFIG.ENABLE_MAPTIVE_OPTIMISED_TERRITORIES
          ? CreateCustomBoundaryMode.MaptiveOptimized
          : CreateCustomBoundaryMode.WMSOptimized
      ));
      dispatch(setActiveMapTemplate(MapTemplates.ATB));
    },
    [MapTemplates.BoundariesDemographic]: () => {
      dispatch(boundaryFormReset());
      dispatch(boundaryFormSetType(BoundaryActionType.LoadGeographicBoundary));
      dispatch(boundaryFormSetPreselected({
        boundaryGroupCodename: BoundaryGroupCodenames.UsZipCodes,
        boundaryTerritoryType: BoundaryTerritoryType.Demographic,
        demographicName: 'Total Population',
      }));
      dispatch(setActiveMapTemplate(MapTemplates.BoundariesDemographic));
    },
    [MapTemplates.BoundariesFill]: () => {
      dispatch(boundaryFormSetType(BoundaryActionType.LoadGeographicBoundary));
      dispatch(boundaryFormSetPreselected({
        boundaryGroupCodename: BoundaryGroupCodenames.UsZipCodes,
        boundaryTerritoryType: BoundaryTerritoryType.Numeric,
        demographicName: null,
      }));
      dispatch(setActiveMapTemplate(MapTemplates.BoundariesFill));
    },
    [MapTemplates.BoundariesPlain]: () => {
      dispatch(boundaryFormSetType(BoundaryActionType.LoadGeographicBoundary));
      dispatch(boundaryFormSetPreselected({
        boundaryGroupCodename: BoundaryGroupCodenames.UsZipCodes,
        boundaryTerritoryType: BoundaryTerritoryType.Manual,
        demographicName: null,
      }));
      dispatch(setActiveMapTemplate(MapTemplates.BoundariesPlain));
    },
    [MapTemplates.DTP]: () => {
      dispatch(setType(ProximityType.DriveTimePolygon));
      dispatch(setTravelTimeHours(1));
      dispatch(setTravelTimeMinutes(10));
      dispatch(setActiveMapTemplate(MapTemplates.DTP));
    },
    [MapTemplates.Filter]: () => {
      dispatch(setActiveMapTemplate(MapTemplates.Filter));
    },
    [MapTemplates.Grouping]: () => {
      dispatch(setActiveMapTemplate(MapTemplates.Grouping));
    },
    [MapTemplates.Heatmaps]: () => {
      dispatch(heatmapFormInitialStyleSet(HeatMapStyle.NumericalData));
      dispatch(setActiveMapTemplate(MapTemplates.Heatmaps));
    },
    [MapTemplates.Layering]: () => {
      dispatch(setActiveMapTemplate(MapTemplates.Layering));
    },
    [MapTemplates.LocationFind]: () => {
      dispatch(setActiveMapTemplate(MapTemplates.LocationFind));
    },
    [MapTemplates.POI]: () => {
      dispatch(setActiveMapTemplate(MapTemplates.POI));
    },
    [MapTemplates.Radii]: () => {
      dispatch(setType(ProximityType.DistanceRadius));
      dispatch(setApplyTo(GroupRadiusEditLevel.Group));
      dispatch(setProximityWithin(50));
      dispatch(setActiveMapTemplate(MapTemplates.Radii));
    },
    [MapTemplates.Routes]: () => {
      dispatch(setActiveMapTemplate(MapTemplates.Routes));
    },
    [MapTemplates.Skip]: noop,
    [MapTemplates.Styling]: () => {
      dispatch(setMapSettingsInitialExpandedIndices([0, 1]));
      dispatch(setActiveMapTemplate(MapTemplates.Styling));
    },
    [MapTemplates.TerritoryManual]: () => {
      dispatch(boundaryFormSetTypeAndMode(BoundaryActionType.CreateTerritories, CreateCustomBoundaryMode.Manual));
      dispatch(setActiveMapTemplate(MapTemplates.TerritoryManual));
    },
  }), [dispatch]);

  return useCallback((template: MapTemplates) => {
    if (template === MapTemplates.Skip) {
      onSkip?.();
      return;
    }
    onTemplateSelected?.(template);
    if (mapId && clientId) {
      createTemplateUsageRecord(clientId, {
        mapId,
        template,
      });
    }
    callbacks[template]?.();
  }, [callbacks, clientId, createTemplateUsageRecord, mapId, onSkip, onTemplateSelected]);
};
